import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi, type AuthUser, type AuthInfo, type PoolStatus } from '@/lib/api';

interface AuthContextType {
  user: AuthUser | null;
  usage: AuthInfo['usage'];
  limits: AuthInfo['limits'];
  pool: PoolStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [usage, setUsage] = useState<AuthInfo['usage']>(null);
  const [limits, setLimits] = useState<AuthInfo['limits']>(null);
  const [pool, setPool] = useState<PoolStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const data = await authApi.me();
      if (data.authenticated && data.user) {
        setUser(data.user);
        setUsage(data.usage);
        setLimits(data.limits);
        setPool(data.pool || null);
      } else {
        setUser(null);
        setUsage(null);
        setLimits(null);
        setPool(null);
      }
    } catch {
      setUser(null);
      setUsage(null);
      setLimits(null);
      setPool(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = () => {
    // Redirect to Google OAuth
    window.location.href = authApi.getGoogleLoginUrl();
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setUsage(null);
      setLimits(null);
      setPool(null);
      window.location.href = '/login';
    }
  };

  const refresh = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        usage,
        limits,
        pool,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
